import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { CheckOutlined, CreditCardOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Flex, Grid, Modal, Segmented, Skeleton, Tag, Typography, theme } from 'antd';
import type { MotivoDeBloqueio, Product, ProductPrice } from '@titularis/api-client';
import { coresDaOferta, estiloDeDestaque, rotuloDoPlano, useColorScheme } from '@titularis/ui';
import { api } from '../api';
import { registrarEvento, type OrigemDaCompra } from '../analytics';
import { ofertaPara, planosDe, reais, useComprar } from '../comprar';
import { ehLei, nomeDoProduto, produto as arteDoProduto, separarLeis } from '../produtos';
import { contar } from '../texto';
import { CapaDoProduto } from './Produto';

/**
 * A oferta: uma tela só, aberta de qualquer lugar, que vende o acesso.
 *
 * **O problema que ela resolve.** Até 05/09/2026 a venda estava espalhada: o
 * paywall dizia "Ver os planos" e navegava, a faixa do topo navegava, a tela de
 * assinatura navegava, e o destino era uma vitrine com um cartão só. Quatro
 * caminhos para a mesma decisão, cada um com um desenho, e todos custando uma
 * troca de tela no instante em que a pessoa estava decidindo. Agora há **um
 * modal**, e quem quer vender chama `abrirOferta()`.
 *
 * **A forma é a do paywall da Voa**, olhado no navegador em 05/09/2026:
 *
 * - à **esquerda a decisão** - o motivo (quando houve bloqueio), o plano num
 *   controle segmentado, as formas de pagamento como cartões selecionáveis,
 *   um botão só e o total embaixo;
 * - à **direita as razões** - o que o acesso abre, item a item, com uma linha
 *   explicando cada um.
 *
 * O que muda em relação ao desenho anterior, que tinha dois botões de preço
 * competindo: **um botão só**. Dois botões primários pedem duas decisões ao
 * mesmo tempo ("comprar?" e "como pagar?"); o cartão selecionável separa as
 * duas, e é por isso que toda tela de plano do mercado usa esse formato.
 *
 * O que **não** foi copiado da Voa: o depoimento de assinante, que lá ocupa
 * metade do painel. Não temos nenhum, e inventar um seria fabricar prova social.
 */

/** O que abriu a oferta, para a mensagem e para o funil. */
export interface PedidoDeOferta {
  /**
   * O produto escolhido **de propósito**, e não deduzido de um bloqueio.
   *
   * Nasceu da comparação das boas-vindas (10/09/2026): ali a pessoa já leu o
   * que aquele produto abre e clicou nele por nome. Deixar o `ofertaPara`
   * escolher de novo, pela chave trancada, poderia abrir a oferta de **outro**
   * produto que também concede a chave - e a tela seguinte contradiria a que
   * acabou de ser lida.
   */
  produto?: string;
  /** De qual superfície a compra partiu, quando não foi um bloqueio que abriu. */
  origemDaCompra?: OrigemDaCompra;
  /**
   * O motivo do 403, quando a oferta nasceu de um bloqueio. Nulo quando a
   * pessoa abriu por vontade própria - pela faixa, pelo menu, por Configurações.
   */
  motivo?: MotivoDeBloqueio | null;
  /** Se o bloqueio foi ao responder ou ao abrir a resolução. */
  origem?: 'answer' | 'commentary';
  /** O que ficou trancado, para escolher qual produto oferecer. */
  chave?: string | null;
  /**
   * O pagamento voltou do provedor sem se concretizar.
   *
   * Desde que o checkout passou a nascer do modal (05/09/2026), a volta também
   * pertence a ele: quem cancelou estava no meio de uma questão, e despejá-lo
   * numa tela de Configurações com um alerta era mandá-lo procurar de novo o
   * botão que acabou de largar. Com o aviso aqui, tentar de novo é um clique.
   */
  aviso?: RetornoDoPagamento | null;
}

/** As duas voltas em que nada foi cobrado. O sucesso tem tela própria. */
export type RetornoDoPagamento = 'cancelado' | 'expirado';

const AVISO: Record<RetornoDoPagamento, { titulo: string; texto: string }> = {
  /* "Nada foi cobrado" abre as duas frases porque é o que a pessoa teme. */
  cancelado: {
    titulo: 'Pagamento cancelado',
    texto: 'Nada foi cobrado. Se quiser, dá para escolher a forma de novo aqui mesmo.',
  },
  expirado: {
    titulo: 'O prazo do pagamento acabou',
    texto: 'Nada foi cobrado. Escolha a forma de novo para gerar uma cobrança nova.',
  },
};

const OfertaContext = createContext<((pedido?: PedidoDeOferta) => void) | null>(null);

/**
 * Abre a oferta de onde estiver.
 *
 * É o que substitui os `navigate('/configuracoes/planos')` espalhados: a venda
 * acontece por cima da tela em que a pessoa está, e fechar devolve o lugar onde
 * ela parou - que era justamente o que o salto para a vitrine tirava dela.
 */
export function useAbrirOferta(): (pedido?: PedidoDeOferta) => void {
  const abrir = useContext(OfertaContext);
  if (!abrir) throw new Error('useAbrirOferta precisa do OfertaProvider por cima.');
  return abrir;
}

export function OfertaProvider({ children }: { children: ReactNode }) {
  const [pedido, setPedido] = useState<PedidoDeOferta | null>(null);

  const abrir = useCallback((novo?: PedidoDeOferta) => setPedido(novo ?? {}), []);
  const valor = useMemo(() => abrir, [abrir]);

  return (
    <OfertaContext.Provider value={valor}>
      {children}
      <ModalDeOferta pedido={pedido} aoFechar={() => setPedido(null)} />
    </OfertaContext.Provider>
  );
}

/* ------------------------------------------------------------- o catálogo */

/** O catálogo à venda, na mesma query que a vitrine e a landing usam. */
export function useCatalogo() {
  return useQuery({
    queryKey: ['billing-products'],
    queryFn: () => api.listProducts(),
    // Preço muda raramente; nenhuma tela precisa perguntar a cada foco.
    staleTime: 5 * 60 * 1000,
  });
}

/* --------------------------------------------------------- o que se diz */

interface Situacao {
  titulo: string;
  texto: string;
}

/**
 * As situações de bloqueio, uma por motivo e origem, cada uma com a sua frase, mais a abertura
 * por vontade própria.
 *
 * Os números saem de `study/status`, nunca escritos aqui: a cota mora em
 * `access_settings` e o dono dela é o backoffice.
 */
const SITUACAO: Record<MotivoDeBloqueio, Record<'answer' | 'commentary', Situacao>> = {
  free_limit: {
    answer: {
      titulo: 'Você respondeu as questões gratuitas desta semana',
      texto:
        'A cota volta na segunda-feira. Com o acesso liberado não há cota semanal: você responde quantas quiser, no ritmo em que estiver estudando.',
    },
    commentary: {
      titulo: 'A cota de resoluções desta semana acabou',
      texto:
        'A resolução é o que separa acertar por sorte de entender a questão - ela traz o dispositivo transcrito e o comentário de cada alternativa, inclusive das que derrubaram você.',
    },
  },
  not_entitled: {
    answer: {
      titulo: 'Este módulo é de quem assina',
      texto:
        'O enunciado continua aberto, porque é público. O gabarito e a resolução comentada fazem parte do acesso pago.',
    },
    commentary: {
      titulo: 'O comentário deste artigo é de quem assina',
      texto:
        'O texto da lei é aberto a qualquer conta - é o mesmo que está no Planalto. O que se cobra é o comentário: o artigo lido por quem corrige prova, com a incidência em concurso e cada citação conferida contra o texto oficial.',
    },
  },
  /*
   * A cota da pessoa pode estar inteira: o que fechou foi a prova. Por isso a
   * frase não fala em semana nem em segunda-feira, e diz o que continua aberto -
   * uma negativa que só fecha manda a pessoa embora.
   */
  /*
   * **"O módulo", e não "o catálogo"** (15/10/2026). A frase prometia "qualquer
   * prova do catálogo" com o acesso liberado, e deixou de ser verdade quando a
   * 1ª fase virou produto separado do Enac: quem compra um não abre o outro.
   */
  not_free_exam: {
    answer: {
      titulo: 'Esta prova faz parte do acesso pago',
      texto:
        'A conta gratuita estuda as provas de amostra, e elas continuam abertas para você. Com o acesso a este módulo, você responde todas as provas dele, com a resolução comentada de cada questão.',
    },
    commentary: {
      titulo: 'A resolução desta prova faz parte do acesso pago',
      texto:
        'A conta gratuita abre as resoluções das provas de amostra. Com o acesso a este módulo, toda prova dele vem com o dispositivo transcrito e o comentário de cada alternativa.',
    },
  },
};

/**
 * O bloqueio de quem já paga por outro módulo.
 *
 * **Nasceu com o segundo produto** (15/10/2026). Para a API, quem tem o Enac e
 * abre uma prova da 1ª fase é gratuito naquele módulo - a decisão é por chave, e
 * está certa -, mas as frases acima falam com a conta gratuita: diziam "a conta
 * gratuita estuda as provas de amostra" a alguém com o selo Pro na barra. O
 * motivo continua escolhendo a conversa; o que muda é para quem ela é dita.
 *
 * Sem nomear o que a pessoa tem nem o módulo trancado: o painel ao lado já diz
 * o que a compra inclui, e o nome dos produtos vem do catálogo, com o gênero
 * que o dono escolheu - "o Enac", "a 1ª Fase" - e nenhum artigo escrito aqui
 * acerta os dois.
 */
const FORA_DO_PLANO: Record<'answer' | 'commentary', Situacao> = {
  answer: {
    titulo: 'Este módulo não está no seu plano',
    texto:
      'O que você já tem continua valendo. Este módulo é vendido à parte: com ele, você responde todas as provas dele, com a resolução comentada de cada questão.',
  },
  commentary: {
    titulo: 'A resolução desta prova é de outro módulo',
    texto:
      'O que você já tem continua valendo. Este módulo é vendido à parte, e com ele toda prova vem com o dispositivo transcrito e o comentário de cada alternativa.',
  },
};

/** Os motivos que dizem "isto é pago", e não "a sua cota acabou". */
const MOTIVOS_DE_ACESSO_PAGO: readonly MotivoDeBloqueio[] = ['not_free_exam', 'not_entitled'];

/**
 * Quando ninguém foi bloqueado: a pessoa veio até aqui, então é venda direta.
 *
 * Dizia "a resolução comentada de todas as questões", e com dois produtos
 * nenhum deles abre todas: a promessa é a do módulo que se compra.
 */
const CONVITE: Situacao = {
  titulo: 'Estude sem limite semanal',
  texto:
    'Com o acesso pago não há cota da semana, e cada questão do módulo vem com a resolução comentada - com o dispositivo transcrito e cada citação conferida contra o texto oficial da lei.',
};

/* ------------------------------------------------------------------ modal */

function ModalDeOferta({
  pedido,
  aoFechar,
}: {
  pedido: PedidoDeOferta | null;
  aoFechar: () => void;
}) {
  const { token } = theme.useToken();
  const telas = Grid.useBreakpoint();
  const aberto = pedido !== null;

  const catalogo = useCatalogo();
  const status = useQuery({
    queryKey: ['study-status'],
    queryFn: () => api.studyStatus(),
    enabled: aberto,
  });

  const oferta = pedido?.produto
    ? catalogo.data?.find((item) => item.key === pedido.produto)
    : catalogo.data
      ? ofertaPara(catalogo.data, pedido?.chave)
      : undefined;
  /** Catálogo carregado, chave bloqueada conhecida, e produto nenhum a concede. */
  const semDono = Boolean(pedido?.chave || pedido?.produto) && Boolean(catalogo.data) && !oferta;
  /**
   * E a mensagem muda com o que ficou sem dono.
   *
   * **O recorte não é detalhe:** a frase da lei fala de legislação, e o bloqueio
   * de uma questão manda a chave do módulo (`enac`, `objective`, `essay`). Sem
   * separar, desativar o produto Enac faria quem esbarrasse no limite semanal de
   * questões ler que "esta legislação não está à venda" - resposta errada para a
   * pergunta errada.
   */
  const SEM_DONO = pedido?.chave?.startsWith('commented_law:')
    ? 'Esta legislação ainda não está à venda em separado. Escreva para a gente se quiser ler os comentários dela.'
    : 'Este acesso não está à venda agora. Escreva para a gente que a gente resolve.';
  const motivo = pedido?.motivo ?? null;
  const aviso = pedido?.aviso ?? null;
  /*
   * Quem chegou por um produto escolhido lê o nome dele, e não o convite.
   *
   * O `CONVITE` é escrito para quem abriu a oferta sem saber o que vem: depois
   * da comparação das boas-vindas, ele repetiria em termos genéricos a tela que
   * a pessoa acabou de ler com o produto na frente.
   */
  /*
   * Quem já paga por outra coisa e esbarrou num módulo que não comprou. Só
   * questão: o bloqueio de um artigo da Legislação Comentada tem frase própria,
   * que já serve a quem assina outras leis.
   */
  const foraDoPlano =
    Boolean(motivo && MOTIVOS_DE_ACESSO_PAGO.includes(motivo)) &&
    Boolean(pedido?.chave && !ehLei(pedido.chave)) &&
    (status.data?.acessos ?? []).some((acesso) => acesso.ativo);
  const conteudo = motivo
    ? (foraDoPlano ? FORA_DO_PLANO : SITUACAO[motivo])[pedido?.origem ?? 'answer']
    : pedido?.produto && oferta
      ? {
          titulo: oferta.name,
          texto:
            oferta.description ??
            'Conheça os conteúdos incluídos neste produto e as condições de acesso.',
        }
      : CONVITE;
  const emDuasColunas = Boolean(telas.md);

  const restante = status.data?.restante ?? { answers: 0, commentaries: 0 };
  const sobra = motivo === 'free_limit' ? frase(restante) : null;

  return (
    <Modal
      open={aberto}
      onCancel={aoFechar}
      centered
      /*
       * 520 é a largura padrão do Antd e é a de um aviso. Uma oferta com preço,
       * formas de pagamento e a lista do que abre precisa das duas colunas para
       * não virar uma escada de blocos empilhados no desktop.
       *
       * **Sem produto para vender, volta a ser um aviso** e volta à largura de
       * aviso: o painel das razões não é renderizado, e 900px com três linhas de
       * texto é um caixote vazio - visto no navegador em 07/09/2026, na lei que
       * nenhum produto concede.
       */
      width={emDuasColunas ? (oferta ? 900 : 520) : '100%'}
      footer={null}
      title={null}
      /*
       * **Sem padding no invólucro, e recortando o que passa das bordas.** É o
       * que permite ao painel das razões ocupar a metade inteira - de borda a
       * borda, do topo ao pé -, como no paywall da Voa. Cada coluna passa a
       * pagar o próprio respiro, e o `overflow` é quem arredonda o canto do
       * painel junto com o canto do modal.
       */
      styles={{
        container: { padding: 0, overflow: 'hidden' },
        body: { padding: 0 },
      }}
    >
      {/*
        **O teto de altura é a rede, não a solução.**

        O que fazia o modal passar da janela era o painel repetindo uma frase por
        lei, e isso foi resolvido na origem. Mas o catálogo cresce sozinho - cada
        lei importada entra num produto -, e uma tela que só cabe enquanto a
        lista for curta volta a quebrar sem avisar. Com o teto, o que sobrar rola
        dentro da coluna em vez de sair pela borda de baixo levando junto o botão
        de comprar. As duas colunas rolam por conta própria porque o `stretch` as
        deixa da mesma altura: prender só uma faria a outra decidir o corte.
      */}
      <Flex
        vertical={!emDuasColunas}
        align="stretch"
        style={emDuasColunas ? { maxHeight: 'calc(100dvh - 48px)' } : undefined}
      >
        <Flex
          vertical
          gap={16}
          style={{
            flex: 1.05,
            minWidth: 0,
            minHeight: 0,
            overflowY: emDuasColunas ? 'auto' : undefined,
            padding: emDuasColunas ? 28 : 20,
          }}
        >
          {/*
            A volta do provedor, quando nada foi cobrado.

            Ela ocupa o lugar do título em vez de somar-se a ele: "Pagamento
            cancelado" **é** o assunto naquele instante, e repetir "Estude sem
            limite semanal" logo abaixo faria a tela falar de duas coisas com a
            pessoa esperando saber se foi cobrada. O `warning`, e não `error`:
            ninguém errou, e nada se perdeu.
          */}
          {aviso ? (
            <Alert
              type="warning"
              showIcon
              message={AVISO[aviso].titulo}
              description={AVISO[aviso].texto}
            />
          ) : (
            <div>
              {/*
                **O respiro à direita é do botão de fechar.** O modal não tem
                `title` do Antd - o cabeçalho é este bloco, dentro do corpo -,
                mas o `×` continua ancorado no canto do modal. No celular o
                título quebra em duas linhas e a primeira passava por baixo dele:
                em 430px, "de quem assina" ficava com o `×` em cima do "é".
                Visto no navegador em 08/09/2026.
              */}
              <Typography.Title level={4} style={{ marginBottom: 6, paddingInlineEnd: 32 }}>
                {conteudo.titulo}
              </Typography.Title>
              <Typography.Text type="secondary">{conteudo.texto}</Typography.Text>
            </div>
          )}

          {/*
            O que ainda sobra nesta semana. Quem foi bloqueado de ler a resolução
            muitas vezes ainda pode responder - e sem esta linha vai embora achando
            que a semana inteira acabou. Some quando não sobra nada, senão vira
            "restam nenhuma questão e nenhuma resolução".
          */}
          {sobra ? (
            <Typography.Text style={{ fontSize: 13, color: token.colorTextTertiary }}>
              Nesta semana ainda {sobra} no plano gratuito.
            </Typography.Text>
          ) : null}

          {oferta ? (
            /*
             * A origem separa a venda que nasceu de um bloqueio da que nasceu
             * de um clique deliberado. Continua sendo a pergunta comercial da
             * rodada - o modal vende mais quando interrompe, ou quando é
             * procurado? -, e agora ela tem resposta mesmo com a oferta sendo
             * uma tela só.
             */
            <Decisao
              key={oferta.key}
              produto={oferta}
              origem={motivo ? 'paywall' : pedido?.origemDaCompra ?? 'oferta'}
              aoFechar={aoFechar}
            />
          ) : catalogo.isLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : semDono ? (
            /*
             * **O cadeado sem preço atrás.** Cada Legislação Comentada é um
             * produto (D-44), e pode existir lei publicada que nenhum produto à
             * venda concede - o backoffice marca essa lei, e aqui a pessoa
             * precisa de uma resposta honesta em vez da oferta de um produto que
             * não abriria o que ela clicou.
             */
            <Typography.Text type="secondary">{SEM_DONO}</Typography.Text>
          ) : (
            <Typography.Text type="secondary">
              Não conseguimos carregar os planos agora. Tente de novo em instantes.
            </Typography.Text>
          )}
        </Flex>

        {oferta ? (
          <Razoes produto={oferta} aoFechar={aoFechar} empilhado={!emDuasColunas} />
        ) : null}
      </Flex>
    </Modal>
  );
}

/* --------------------------------------------------- a coluna da decisão */

/**
 * O plano num controle segmentado, as formas como cartões selecionáveis e um
 * botão só.
 *
 * **Por que não os dois botões de preço que existiam antes.** Eles pediam duas
 * decisões ao mesmo tempo - "comprar?" e "como pagar?" - com o mesmo peso
 * visual, e o segundo botão roubava a atenção do primeiro. O cartão
 * selecionável separa as duas: escolher é barato, comprar é o botão.
 *
 * **O plano mais longo e o Pix nascem escolhidos.** Não é neutralidade: o mais
 * longo tem o melhor equivalente mensal, e o Pix custa taxa fixa enquanto o
 * cartão custa percentual. É onde queremos o olho antes da decisão de compra.
 */
function Decisao({
  produto,
  origem,
  aoFechar,
}: {
  produto: Product;
  origem: OrigemDaCompra;
  aoFechar: () => void;
}) {
  const { token } = theme.useToken();
  const compra = useComprar(origem);

  const planos = useMemo(() => planosDe(produto), [produto]);
  const [meses, setMeses] = useState<number | undefined>(planos.at(-1)?.accessMonths);
  const plano = planos.find((item) => item.accessMonths === meses) ?? planos.at(-1);
  const [priceId, setPriceId] = useState<string | undefined>(plano?.precos[0]?.id);
  const escolhido = plano?.precos.find((preco) => preco.id === priceId) ?? plano?.precos[0];

  /** Quanto o Pix economiza contra o cartão do mesmo plano. É o argumento do desconto. */
  const pix = plano?.precos.find((preco) => preco.kind === 'pix');
  const cartao = plano?.precos.find((preco) => preco.kind === 'card');
  const economia =
    pix && cartao
      ? Math.max(0, cartao.amountCents - pix.amountCents)
      : 0;

  if (!plano || !escolhido) return null;

  return (
    <Flex vertical gap={token.marginSM}>
      <Flex vertical gap={token.marginXS}>
        <Typography.Text strong>Plano</Typography.Text>
        <Segmented
          block
          aria-label="Plano"
          value={plano.accessMonths}
          options={planos.map((item) => ({
            label: rotuloDoPlano(item.accessMonths),
            value: item.accessMonths,
          }))}
          onChange={(accessMonths) => {
            const proximo = planos.find((item) => item.accessMonths === accessMonths);
            setMeses(accessMonths);
            setPriceId(proximo?.precos[0]?.id);
          }}
        />
      </Flex>

      <Flex vertical gap={token.marginXS} role="radiogroup" aria-label="Forma de pagamento">
        <Typography.Text strong>Forma de pagamento</Typography.Text>
        {plano.precos.map((preco) => (
          <CartaoDePreco
            key={preco.id}
            preco={preco}
            escolhido={preco.id === escolhido.id}
            // O desconto pertence ao Pix, e só existe quando há cartão comparável.
            economia={preco.id === pix?.id ? economia : 0}
            aoEscolher={() => setPriceId(preco.id)}
          />
        ))}
      </Flex>

      {/*
        **O total é uma gaveta debaixo do botão**, e não uma linha solta com
        respiro dos dois lados. É a construção da Voa, medida nela: a barra
        encosta no pé do botão, é dezesseis pixels mais estreita e só arredonda
        os cantos de baixo - o desenho de um comprovante saindo de dentro dele.

        Ela existe porque o cartão escolhido mostra o valor **por mês**: sem
        esta linha, quem escolhe o parcelado não vê em lugar nenhum quanto vai
        pagar no fim.
      */}
      <div>
        <Button
          type="primary"
          size="large"
          block
          loading={compra.emAndamento === `${produto.key}:${escolhido.id}`}
          disabled={compra.ocupado}
          onClick={() => {
            aoFechar();
            compra.comprar({
              produto: produto.key,
              priceId: escolhido.id,
              forma: escolhido.kind as 'card' | 'pix',
              meses: escolhido.accessMonths,
              recorrente: escolhido.recurring,
            });
          }}
          style={{ borderRadius: token.borderRadiusLG, position: 'relative', zIndex: 1 }}
        >
          Continuar
        </Button>

        <Flex
          align="center"
          justify="space-between"
          gap={12}
          style={{
            marginInline: 8,
            paddingBlock: 10,
            paddingInline: 14,
            background: token.colorFillTertiary,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderTop: 'none',
            borderRadius: `0 0 ${token.borderRadiusLG}px ${token.borderRadiusLG}px`,
          }}
        >
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {plano.recurring ? 'Por mês' : 'Total'}
          </Typography.Text>
          <Typography.Text strong>
            {reais(escolhido.amountCents)}
            {!plano.recurring
              ? escolhido.maxInstallments > 1
                ? ` em ${escolhido.maxInstallments}x`
                : ' à vista'
              : null}
          </Typography.Text>
        </Flex>
      </div>

      {/*
        A renovação fica junto da escolha que a provoca, e não na coluna das
        vantagens. Agora há planos recorrentes e compras únicas no mesmo
        produto; uma frase fixa do outro lado mentiria assim que o plano mudasse.
      */}
      <Typography.Text
        type="secondary"
        style={{
          alignSelf: 'center',
          fontSize: token.fontSizeSM,
          textAlign: 'center',
        }}
      >
        {plano.recurring
          ? 'Renova automaticamente. Você pode cancelar quando quiser.'
          : 'Compra única, não renova sozinha.'}
      </Typography.Text>
    </Flex>
  );
}

/**
 * Um cartão de forma de pagamento.
 *
 * No cartão, o número grande é a parcela mensal. No Pix, o número grande é o
 * total pago à vista e a equivalência mensal fica na linha auxiliar. Assim a
 * hierarquia acompanha o que de fato será cobrado em cada forma de pagamento.
 */
function CartaoDePreco({
  preco,
  escolhido,
  economia,
  aoEscolher,
}: {
  preco: ProductPrice;
  escolhido: boolean;
  economia: number;
  aoEscolher: () => void;
}) {
  const { token } = theme.useToken();

  const cartao = preco.kind === 'card';
  const parcelado = !preco.recurring && preco.maxInstallments > 1;
  const porMes = Math.round(preco.amountCents / preco.accessMonths);

  return (
    <Flex
      vertical
      gap={2}
      role="radio"
      aria-checked={escolhido}
      tabIndex={0}
      onClick={aoEscolher}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter' || evento.key === ' ') {
          evento.preventDefault();
          aoEscolher();
        }
      }}
      style={{
        /*
         * **Um fio só, na cor da marca, e sem fundo tingido** - é o contorno da
         * Voa, medido nela: 0,8px de borda e o cartão transparente, com o
         * estado dito pelo marcador ao lado do nome. O desenho anterior tinha
         * borda de 2px **e** fundo `colorPrimaryBg`, e ainda assim precisava de
         * um pixel de margem para o cartão não pular quando a borda engrossava.
         */
        padding: '12px 16px',
        cursor: 'pointer',
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${escolhido ? token.colorPrimary : token.colorBorderSecondary}`,
        transition: 'border-color 0.2s',
      }}
    >
      <Flex align="center" justify="space-between" gap={8}>
        <Flex align="center" gap={8}>
          <Marcador escolhido={escolhido} />
          {cartao ? <CreditCardOutlined /> : <ThunderboltOutlined />}
          <Typography.Text strong>{cartao ? 'Cartão' : 'Pix'}</Typography.Text>
        </Flex>
        {economia > 0 ? (
          <Tag color="green" style={{ marginInlineEnd: 0 }}>
            Economize {reais(economia)}
          </Tag>
        ) : null}
      </Flex>

      <Flex align="baseline" gap={6} wrap>
        <span style={{ ...estiloDeDestaque(26), color: token.colorText }}>
          {reais(parcelado ? Math.round(preco.amountCents / preco.maxInstallments) : preco.amountCents)}
        </span>
        <Typography.Text type="secondary">
          {preco.recurring ? 'por mês' : parcelado ? 'por mês' : 'à vista'}
        </Typography.Text>
      </Flex>

      <Typography.Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
        {preco.recurring
          ? 'Cobrança mensal no cartão, sem parcelamento'
          : parcelado
            ? `${preco.maxInstallments}x de ${reais(Math.round(preco.amountCents / preco.maxInstallments))}, total de ${reais(preco.amountCents)}`
            : preco.accessMonths > 1
              ? `equivalente a ${reais(porMes)} por mês`
              : 'Pagamento único'}
      </Typography.Text>
    </Flex>
  );
}

/**
 * A marca de cada coisa que o acesso abre.
 *
 * **Disco com degradê e o risco em branco por cima**, e não o
 * `CheckCircleFilled` monocromático que estava aqui: o degradê é o mesmo da
 * chamada da barra lateral - as três cores dos produtos -, e é o que dá ao
 * paywall a assinatura visual que a Voa tem com o roxo dela.
 *
 * A cor é **uma só para os três itens**, e não a de cada produto: em 16px de
 * disco, três cores diferentes viram confete, e o que a lista precisa dizer é
 * "isto vem junto", não "isto é aquele produto".
 */
function MarcaDeInclusao() {
  const [verde, azul, ameixa] = coresDaOferta;

  return (
    <span
      aria-hidden
      style={{
        width: 20,
        height: 20,
        marginTop: 2,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        /*
         * As três cores da chamada, na diagonal: o verde no canto de cima, a
         * ameixa no de baixo. Nos tons originais, e não nos clareados - o risco
         * branco em cima precisa de fundo escuro, e sobre o verde claro
         * (`#33b7b1`) ele cairia para 2,1:1.
         */
        background: `linear-gradient(135deg, ${verde}, ${azul}, ${ameixa})`,
        color: '#fff',
        fontSize: 10,
      }}
    >
      <CheckOutlined />
    </span>
  );
}

/**
 * O ponto que diz qual forma está escolhida.
 *
 * **Desenhado, e não um `Radio` do Antd**, porque o controle é o cartão
 * inteiro: um `input` de verdade aqui seria um segundo alvo de clique dentro
 * de outro, e o leitor de tela anunciaria dois rádios para a mesma escolha. O
 * cartão já é `role="radio"` com `aria-checked`; este é o desenho dele, e por
 * isso vem `aria-hidden`.
 *
 * O anel interno na cor do fundo é o que transforma o disco cheio no ponto
 * clássico do rádio - o mesmo truque do Antd, sem o componente.
 */
function Marcador({ escolhido }: { escolhido: boolean }) {
  const { token } = theme.useToken();

  return (
    <span
      aria-hidden
      style={{
        width: 16,
        height: 16,
        flexShrink: 0,
        borderRadius: '50%',
        border: `1.5px solid ${escolhido ? token.colorPrimary : token.colorBorder}`,
        background: escolhido ? token.colorPrimary : token.colorBgContainer,
        boxShadow: escolhido ? `inset 0 0 0 3px ${token.colorBgContainer}` : undefined,
        transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
      }}
    />
  );
}

/* ---------------------------------------------------- a coluna das razões */

/** O que o acesso abre, item a item - e a saída macia no rodapé. */
function Razoes({
  produto,
  aoFechar,
  empilhado,
}: {
  produto: Product;
  aoFechar: () => void;
  empilhado: boolean;
}) {
  const { token } = theme.useToken();
  const { scheme } = useColorScheme();
  const escuro = scheme === 'dark';
  /*
   * A capa pela chave do produto e, sem ela, pela única chave que ele concede.
   * O produto da 1ª fase nasceu como `primeira_fase` e concede `objective`: a
   * chave do produto é escolha comercial, e a arte é da fase que ele abre.
   */
  const arte =
    arteDoProduto(produto.key) ??
    (produto.grants.length === 1 ? arteDoProduto(produto.grants[0]!) : undefined);

  /*
   * A lista só aparece quando ela diz alguma coisa - a mesma regra da vitrine.
   * Um produto que libera só a si mesmo viraria uma lista de um item com o nome
   * do próprio produto logo acima: ruído com aparência de conteúdo.
   */
  const inclusos =
    produto.grants.length === 1 && produto.grants[0] === produto.key ? [] : produto.grants;
  /*
   * **As leis entram como um item só.**
   *
   * O Enac abre sete Legislações Comentadas, e cada uma repetia aqui o mesmo
   * "Esta lei, comentada artigo a artigo." - sete blocos de duas linhas para
   * dizer uma regra. O painel passava dos 790px numa janela de 770 e o modal
   * saía cortado embaixo, com o "Agora não" fora da tela (visto em 10/09/2026).
   *
   * Cortar por rolagem esconderia o argumento; o que sobrava eram sete cópias
   * de uma frase. Agora a regra é dita uma vez e os nomes vão embaixo, em
   * linha corrida - continua dizendo **quais** leis, que é a única coisa que
   * mudava de bloco para bloco, e o painel volta a caber.
   */
  const { leis, outros } = separarLeis(inclusos);

  return (
    <Flex
      vertical
      gap={16}
      style={{
        flex: 1,
        minWidth: 0,
        // Ver o teto de altura no invólucro: a coluna rola por conta própria.
        minHeight: 0,
        overflowY: empilhado ? undefined : 'auto',
        padding: empilhado ? 20 : 28,
        /*
         * **A metade inteira, sem contorno.** Era um cartão apagado flutuando
         * dentro do modal, com borda e raio próprios, e sobrava uma moldura de
         * fundo entre ele e a borda - dois retângulos, um dentro do outro. Na
         * Voa o cinza vai de borda a borda e do topo ao pé, e é ele mesmo quem
         * divide o modal em duas coisas: sem fio, sem raio, sem moldura. O
         * `overflow: hidden` do invólucro é quem corta o canto.
         *
         * **No escuro ele fica preto**, e não um cinza mais claro que o modal:
         * o `colorFillTertiary` é um véu branco por cima, então a metade das
         * razões saía *acima* da decisão na escala de luz - o contrário do
         * claro, onde ela recua. `colorBgLayout` no escuro é `#000`, e é o
         * mesmo recuo que a Voa faz.
         */
        background: escuro ? token.colorBgLayout : token.colorFillTertiary,
      }}
    >
      {/*
        A capa entra pequena, e não como cartaz.

        Em tamanho de vitrine ela ocupava quase duzentos pixels de altura e
        empurrava as vantagens - que são o que vende - para o fim do painel. O
        produto precisa ter rosto (é a decisão de 02/09 sobre as capas), mas
        aqui ele é assinatura, não vitrine. No empilhado some de vez: num
        celular ela jogaria o botão de comprar para baixo da dobra.
      */}
      {arte?.capa && !empilhado ? (
        <div style={{ maxWidth: 168 }}>
          <CapaDoProduto produto={arte} />
        </div>
      ) : null}

      <div>
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}
        >
          {/* Sem artigo: o nome vem do catálogo, e "o 1ª Fase" foi o que saiu
              do "O que o ... abre" no primeiro produto feminino. */}
          O que está incluído em {produto.name}
        </Typography.Text>
      </div>

      <Flex vertical gap={14}>
        {outros.map((chave) => {
          // O rótulo do servidor nomeia o que o bundle não conhece: cada
          // Legislação Comentada é uma chave que nasce sem passar por aqui (D-45).
          const dele = arteDoProduto(chave, produto.rotulos[chave]);
          return (
            <Flex key={chave} align="flex-start" gap={10}>
              <MarcaDeInclusao />
              {/*
                Nome **e** uma linha explicando, como a Voa faz em cada vantagem.
                Uma lista de nomes soltos não diz o que a pessoa ganha:
                "Legislação Comentada" é rótulo, "a legislação de cartórios
                comentada artigo a artigo" é oferta. O texto já existia em
                `produtos.ts`.
              */}
              <Flex vertical style={{ minWidth: 0 }}>
                <Typography.Text strong>
                  {nomeDoProduto(chave, produto.rotulos[chave])}
                </Typography.Text>
                {dele?.resumo ? (
                  <Typography.Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
                    {dele.resumo}
                  </Typography.Text>
                ) : null}
              </Flex>
            </Flex>
          );
        })}

        {leis.length ? (
          <Flex align="flex-start" gap={10}>
            <MarcaDeInclusao />
            <Flex vertical style={{ minWidth: 0 }}>
              <Typography.Text strong>
                {contar(leis.length, 'lei comentada', 'leis comentadas')}
              </Typography.Text>
              <Typography.Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
                Artigo a artigo: {leis.map((chave) => produto.rotulos[chave] ?? chave).join(' · ')}
              </Typography.Text>
            </Flex>
          </Flex>
        ) : null}

        <Flex align="flex-start" gap={10}>
          <MarcaDeInclusao />
          <Flex vertical style={{ minWidth: 0 }}>
            <Typography.Text strong>Período de acesso do plano escolhido</Typography.Text>
            <Typography.Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
              Inclui tudo o que for publicado durante o período contratado.
            </Typography.Text>
          </Flex>
        </Flex>
      </Flex>

      {/* A saída fica aqui embaixo, como o "Deixar para depois" da Voa: longe do
          botão que compra, e sem competir com ele. */}
      <Button
        type="text"
        onClick={aoFechar}
        style={{
          alignSelf: 'flex-end',
          marginInlineEnd: -15,
          marginTop: 'auto',
        }}
      >
        Agora não
      </Button>
    </Flex>
  );
}

/* ------------------------------------------------------------- auxiliares */

/**
 * "resta 1 resolução", "restam 2 questões e 1 resolução", ou nada.
 *
 * Lista só o que sobrou, e concorda o verbo com o que restou na lista: dois
 * itens são sujeito composto e pedem plural, um item pede o número dele. Zero
 * devolve `null`, e a linha inteira desaparece.
 */
function frase(restante: { answers: number; commentaries: number }): string | null {
  const partes: string[] = [];
  if (restante.answers > 0) partes.push(contar(restante.answers, 'questão', 'questões'));
  if (restante.commentaries > 0) {
    partes.push(contar(restante.commentaries, 'resolução', 'resoluções'));
  }

  if (partes.length === 0) return null;

  const plural = partes.length > 1 || restante.answers > 1 || restante.commentaries > 1;
  return `${plural ? 'restam' : 'resta'} ${partes.join(' e ')}`;
}

/**
 * Registra o bloqueio e abre a oferta, que é sempre o mesmo par.
 *
 * Existe para as telas de estudo não repetirem as duas linhas - e para o
 * `paywall_shown` não ficar de fora quando alguém acrescentar um bloqueio novo.
 */
export function useBloqueio() {
  const abrirOferta = useAbrirOferta();

  return useCallback(
    (pedido: Required<Pick<PedidoDeOferta, 'motivo' | 'origem'>> & { chave?: string | null }) => {
      registrarEvento({
        nome: 'paywall_shown',
        motivo: pedido.motivo!,
        origem: pedido.origem,
        produto: pedido.chave ?? null,
      });
      abrirOferta(pedido);
    },
    [abrirOferta],
  );
}
